import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import ConfirmModal from '../../components/ConfirmModal';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { Trash2, HardDrive, Image, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';

const UPLOADS_BASE = process.env.REACT_APP_UPLOADS_URL || '';

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function AdminStorage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [filter, setFilter] = useState('all'); // all | used | unused
  const [deleteTarget, setDeleteTarget] = useState(null); // filename | 'bulk'
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchStorage(); }, []);

  const fetchStorage = async () => {
    try {
      setLoading(true);
      const res = await API.get('/upload/admin/storage');
      setFiles(res.data.files || []);
      setTotalSize(res.data.totalSize || 0);
    } catch {
      toast.error('Gagal memuat data storage');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      if (deleteTarget === 'bulk') {
        const res = await API.delete('/upload/admin/storage');
        toast.success(res.data.message);
      } else {
        await API.delete(`/upload/admin/storage/${deleteTarget}`);
        toast.success('File berhasil dihapus');
      }
      fetchStorage();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menghapus');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const unusedFiles = files.filter(f => !f.inUse);
  const usedFiles = files.filter(f => f.inUse);
  const displayed = filter === 'used' ? usedFiles : filter === 'unused' ? unusedFiles : files;

  const unusedSize = unusedFiles.reduce((s, f) => s + f.size, 0);

  return (
    <DashboardLayout title="Manajemen Storage" subtitle="Kelola file foto yang tersimpan di server">

      {/* Summary cards */}
      <div className="stats-row" style={{ marginBottom: 24 }}>
        {[
          { label: 'Total File', val: files.length, icon: <Image size={20} />, bg: 'rgba(0,21,64,0.08)' },
          { label: 'Total Ukuran', val: formatBytes(totalSize), icon: <HardDrive size={20} />, bg: 'rgba(0,21,64,0.08)' },
          { label: 'Sedang Dipakai', val: usedFiles.length, icon: <CheckCircle size={20} />, bg: 'rgba(0,21,64,0.08)' },
          { label: 'Tidak Terpakai', val: unusedFiles.length, icon: <AlertCircle size={20} />, bg: 'rgba(212,105,10,0.10)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-card-glow" style={{ background: s.bg }} />
            <div>{s.icon}</div>
            <div className="stat-val" style={{ fontSize: unusedFiles.length > 0 && s.label === 'Tidak Terpakai' ? 20 : undefined }}>
              {loading ? '...' : s.val}
            </div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="ki-tabs" style={{ flex: 1 }}>
          {[
            { key: 'all', label: `Semua (${files.length})` },
            { key: 'used', label: `Dipakai (${usedFiles.length})` },
            { key: 'unused', label: `Tidak Terpakai (${unusedFiles.length})` },
          ].map(t => (
            <button key={t.key} className={`ki-tab ${filter === t.key ? 'active' : ''}`} onClick={() => setFilter(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={fetchStorage} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </button>
        {unusedFiles.length > 0 && (
          <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget('bulk')} disabled={loading}>
            <Trash2 size={14} /> Hapus Semua Tidak Terpakai ({formatBytes(unusedSize)})
          </button>
        )}
      </div>

      {/* File list */}
      {loading ? (
        <div className="ki-card">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="shimmer" style={{ height: 72, borderRadius: 8, marginBottom: 10 }} />
          ))}
        </div>
      ) : displayed.length === 0 ? (
        <div className="ki-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <Image size={40} style={{ color: 'var(--ki-text-faint)', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Tidak ada file</div>
          <p style={{ color: 'var(--ki-text-muted)', fontSize: 13 }}>
            {filter === 'unused' ? 'Semua file sedang digunakan.' : 'Belum ada file yang diupload.'}
          </p>
        </div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead>
              <tr>
                <th style={{ width: 72 }}>Preview</th>
                <th>Nama File</th>
                <th>Ukuran</th>
                <th>Status</th>
                <th>Tanggal Upload</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(f => (
                <tr key={f.filename}>
                  <td>
                    <img
                      src={`${UPLOADS_BASE}/uploads/${f.filename}`}
                      alt={f.filename}
                      style={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 6, background: 'var(--ki-bg)' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  </td>
                  <td>
                    <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--ki-text-muted)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.filename}
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-muted)', whiteSpace: 'nowrap' }}>
                    {formatBytes(f.size)}
                  </td>
                  <td>
                    {f.inUse ? (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: 'rgba(52,211,153,0.15)', color: '#10b981' }}>
                        Dipakai
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: 'rgba(248,113,113,0.15)', color: '#ef4444' }}>
                        Tidak Terpakai
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--ki-text-faint)', whiteSpace: 'nowrap' }}>
                    {new Date(f.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setDeleteTarget(f.filename)}
                      title="Hapus file"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title={deleteTarget === 'bulk' ? 'Hapus Semua File Tidak Terpakai' : 'Hapus File'}
        message={
          deleteTarget === 'bulk'
            ? `Akan menghapus ${unusedFiles.length} file (${formatBytes(unusedSize)}) yang tidak digunakan oleh artikel maupun pengguna manapun. Tindakan ini tidak dapat dibatalkan.`
            : `Hapus file "${deleteTarget}"? Pastikan file ini tidak lagi dibutuhkan. Tindakan ini tidak dapat dibatalkan.`
        }
        confirmLabel="Hapus"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />
    </DashboardLayout>
  );
}
